// ═══════════════════════════════════════════════════════════════════════
// Temps › À traiter (RH) — la boîte unique (conception § 14.2), vue RH : étape 2 des congés,
// demandes d'annulation, anomalies sans N+1 ou escaladées, demandes du temps et autorisations sans
// N+1, certificats à vérifier, régularisations tardives. Lignes et actions renvoyées par le serveur
// (services/temps/aTraiter.js, agent H). Deux verbes : « Accepter » (un clic) et « Refuser » (motif).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Inbox, Check, X, ClipboardCheck, Scale, Archive, CheckCircle2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Pagination } from '../../design/composants';
import { NavTemps, useDroitsTemps, AccesTemps, lireRh, ecrireRh, messageErreur, jourCourt } from './rhCommun';
// Fenêtres de décision partagées avec la boîte du responsable (agent H).
import { FenetreMotif, FenetreQualifier, FenetreArbitrer, signalerATraiter } from './EquipeCommun';
// Suivi « prochain acteur » (champ `suivi` des lignes, vue gestion) : qui doit agir et depuis quand.
import { LigneSuivi } from '../../components/suivi/SuiviDemande';

const RAISONS = {
  SANS_N1: 'Sans responsable N+1 habilité', ESCALADE: 'Confiée à la RH par le responsable', TYPE_RH: 'Relève de la RH',
  CONFLIT_ABSENCE_DECLAREE: 'Conflit avec une absence déclarée',
};

export default function RhATraiterPage() {
  const droits = useDroitsTemps();
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [taille, setTaille] = useState(25);
  const [donnees, setDonnees] = useState(null);
  const [referentiel, setReferentiel] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const [enCours, setEnCours] = useState(null);

  const charger = useCallback(async () => {
    setErreur('');
    try { setDonnees(await lireRh('/a-traiter', { type: type || undefined, page, taille })); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [type, page, taille]);
  useEffect(() => { if (droits?.rh?.menu?.a_traiter) charger(); }, [droits, charger]);
  useEffect(() => { if (droits?.rh?.menu?.a_traiter) lireRh('/a-traiter/referentiel').then(setReferentiel).catch(() => {}); }, [droits]);

  const termine = (m) => { setFenetre(null); setEnCours(null); setMessage(m); signalerATraiter(); charger(); };
  const chemin = (l, verbe) => `/a-traiter/${l.type}/${l.id}/${verbe}`;
  const accepter = async (l) => {
    setEnCours(l.cle); setErreur(''); setMessage('');
    try {
      const r = await ecrireRh('post', chemin(l, 'accepter'), {});
      termine(r?.message || `${l.libelle} : accepté.`);
    } catch (e) { setErreur(messageErreur(e)); setEnCours(null); }
  };
  const types = referentiel?.types || [];
  const libelleAction = (l, a) => (l.libelles_actions && l.libelles_actions[a]) || (referentiel?.actions || {})[a] || a;

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'À traiter' }]} titre="À traiter"
          sousTitre="Seules les exceptions qui attendent une décision de la RH. Les journées normales n'apparaissent jamais ici." />
        <NavTemps actif="a_traiter" />
        <AccesTemps droits={droits} rubrique="a_traiter">
          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
          {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
          {donnees && donnees.total_general > 0 && (
            <div className="rh-vues rh-vues-ligne" role="group" aria-label="Type d'élément">
              <button type="button" className="rh-vue" aria-pressed={!type} onClick={() => { setType(''); setPage(1); }}>Tout <span className="rh-vue-compte">{donnees.total_general}</span></button>
              {Object.entries(donnees.par_type).map(([code, n]) => (
                <button key={code} type="button" className="rh-vue" aria-pressed={type === code} onClick={() => { setType(code); setPage(1); }}>
                  {(types.find((t) => t.code === code) || {}).libelle || code} <span className="rh-vue-compte">{n}</span>
                </button>
              ))}
            </div>
          )}
          {!donnees && !erreur && <Squelette largeur="60%" />}
          {donnees && (
            <Carte titre={`${donnees.total} élément${donnees.total > 1 ? 's' : ''} à traiter`} Icone={Inbox}>
              {!donnees.lignes.length ? <EtatVide Icone={CheckCircle2} titre="Rien à traiter" texte="Tout est à jour dans votre périmètre." /> : (
                <ul className="rh-liste">
                  {donnees.lignes.map((l) => {
                    const raison = l.raison_rh ? RAISONS[l.raison_rh] : l.sans_n1 ? RAISONS.SANS_N1 : null;
                    return (
                      <li key={l.cle} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <strong style={{ fontWeight: 500 }}>{l.libelle}</strong>
                            <Badge ton="neutre">{l.categorie}</Badge>
                            {raison && <Badge ton="alerte" title="Décision prise sans le regard du responsable : elle figurera dans la revue des actes sensibles.">{raison}</Badge>}
                          </div>
                          <div className="rh-cellule-secondaire">{l.detail}{l.salarie?.matricule ? ` · ${l.salarie.matricule}` : ''}{l.date ? ` · ${jourCourt(l.date)}` : ''}</div>
                          <LigneSuivi suivi={l.suivi} />
                          {l.aide?.length > 0 && (
                            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--sg-texte-2)' }}>{l.aide.map((a) => <li key={a}>{a}</li>)}</ul>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {l.actions.includes('ACCEPTER') && (l.simple || l.type === 'CERTIFICAT') && !l.motif_acceptation && (
                            <Bouton petit variante="primaire" Icone={Check} disabled={enCours === l.cle} onClick={() => accepter(l)}>{libelleAction(l, 'ACCEPTER')}</Bouton>
                          )}
                          {l.actions.includes('ACCEPTER') && l.motif_acceptation && (
                            <Bouton petit variante="primaire" Icone={Check} onClick={() => setFenetre({ genre: 'motif', ligne: l, verbe: 'accepter', titre: `Accepter — ${l.libelle}`, bouton: 'Accepter', variante: 'primaire', Icone: Check, aide: l.aide_acceptation || 'Motif de l\'annulation, communiqué au salarié.' })}>{libelleAction(l, 'ACCEPTER')}</Bouton>
                          )}
                          {l.actions.includes('ACCEPTER') && !l.simple && l.type === 'DEMANDE_TEMPS' && l.qualifications?.length > 0 && (
                            <Bouton petit variante="primaire" Icone={Check} onClick={() => setFenetre({ genre: 'qualifier', ligne: l, verbe: 'accepter', titre: 'Accepter' })}>{libelleAction(l, 'ACCEPTER')}…</Bouton>
                          )}
                          {l.actions.includes('QUALIFIER') && (
                            <Bouton petit variante="primaire" Icone={ClipboardCheck} onClick={() => setFenetre({ genre: 'qualifier', ligne: l, verbe: 'qualifier', titre: 'Régler' })}>{libelleAction(l, 'QUALIFIER')}</Bouton>
                          )}
                          {l.actions.includes('ARBITRER') && (
                            <Bouton petit Icone={Scale} onClick={() => setFenetre({ genre: 'arbitrer', ligne: l })}>{libelleAction(l, 'ARBITRER')}</Bouton>
                          )}
                          {l.actions.includes('CLASSER') && (
                            <Bouton petit variante="discret" Icone={Archive} onClick={() => setFenetre({ genre: 'motif', ligne: l, verbe: 'classer', titre: `Classer — ${l.libelle}`, bouton: 'Classer', variante: 'primaire', Icone: Archive, aide: 'Pourquoi ce point est-il sans suite ?' })}>{libelleAction(l, 'CLASSER')}</Bouton>
                          )}
                          {l.actions.includes('REFUSER') && (
                            <Bouton petit variante="discret" Icone={X} onClick={() => setFenetre({ genre: 'motif', ligne: l, verbe: 'refuser', titre: `${libelleAction(l, 'REFUSER')} — ${l.libelle}`, bouton: libelleAction(l, 'REFUSER'), variante: 'danger', Icone: X })}>{libelleAction(l, 'REFUSER')}</Bouton>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Pagination page={donnees.page} pages={donnees.pages} total={donnees.total} taille={donnees.taille}
                onPage={(p) => setPage(Math.min(Math.max(1, p), donnees.pages))} onTaille={(t) => { setTaille(t); setPage(1); }} tailles={[10, 25, 50, 100]} />
            </Carte>
          )}

          {fenetre?.genre === 'motif' && (
            <FenetreMotif titre={fenetre.titre} aide={fenetre.aide} libelleBouton={fenetre.bouton} variante={fenetre.variante} Icone={fenetre.Icone}
              onFermer={() => setFenetre(null)}
              onValider={async (motif) => {
                const l = fenetre.ligne;
                const url = fenetre.verbe === 'classer' ? `/a-traiter/ANOMALIE/${l.id}/classer` : chemin(l, fenetre.verbe);
                await ecrireRh('post', url, { motif });
                termine(fenetre.verbe === 'refuser' ? `${l.libelle} : refusé. Le salarié est prévenu avec le motif.` : `${l.libelle} : enregistré.`);
              }} />
          )}
          {fenetre?.genre === 'qualifier' && (
            <FenetreQualifier ligne={fenetre.ligne} referentiel={referentiel} titre={fenetre.titre} onFermer={() => setFenetre(null)}
              onValider={async (corps) => {
                const l = fenetre.ligne;
                await ecrireRh('post', fenetre.verbe === 'qualifier' ? `/a-traiter/ANOMALIE/${l.id}/qualifier` : chemin(l, 'accepter'), corps);
                termine(`${l.libelle} : réglé.`);
              }} />
          )}
          {fenetre?.genre === 'arbitrer' && (
            <FenetreArbitrer ligne={fenetre.ligne} onFermer={() => setFenetre(null)}
              onValider={async (corps) => { await ecrireRh('post', `/a-traiter/ANOMALIE/${fenetre.ligne.id}/arbitrer`, corps); termine(`${fenetre.ligne.libelle} : décision enregistrée.`); }} />
          )}
        </AccesTemps>
      </div>
    </Layout>
  );
}
